import * as React from 'react';
import { cloneDeep, debounce } from 'lodash';
import { DeleteOutlined } from '@ant-design/icons';
import { message, Popconfirm, Space, Switch } from 'antd';
import {
  ValueCheck,
  DiffStates,
  Reference,
  ReferenceNode,
  FrameItem,
  ParameterType,
  ParameterConfig,
  Attribute,
  valueCheckToServer,
  PromptInfo,
  PromptType,
  PromptOwnerType,
  InputParameter,
  ActionType,
  FramelyAction,
  ClearSlotAction
} from '@interface/index';
import {
  NiSpin,
  BodyGroup,
  NiTable,
  NiModal,
  AddButton,
  PromptListComp,
  NiForm,
  NiSelect,
  discardModal,
  InputParameterComp,
  NiRadio,
  ReferenceTextArea,
  NiCard,
  NiButton,
  HelperPopover,
  sourceRender
} from '@component/index';
import { NiCommonCascader, SELECTION_KEY, SelectionOption, API, SourceType, PAGE_TYPE } from '@config/index';
import { localizedString } from '@locale/index';
import {
  ParameterMapStore,
  isEqual,
  attributeToParameter,
  getAgentId,
  isStructAgent
} from '@lib/index';
import { AnnotationOperator } from '@page/action-parameter/component/annotation-operator';

import '@style/component/value-check/index.less';
import { NewPromptList, PromptListHandle } from '@component/prompt/new-prompt-list';
import CodeTextArea from '@component/code-textarea/code-textarea';
import EmptyLine from '@component/business-component/empty-line/empty-line';

interface ValueCheckTableProps {
  disabled?: boolean;
  frame: FrameItem;
  forSlot?: Attribute;
  data: ValueCheck[];
  parameters: ParameterConfig[];
  selectedParameters: Attribute[];
  didChange?: (data: ValueCheck[]) => void;
  onChange?: (data: ValueCheck[]) => void;
  cardDeleteClick?: () => void;

  showSwitch?: boolean;
  switchChecked?: boolean;
  onSwitchChange?: (checked: boolean) => void;
}

interface ValueCheckTableState {
  isNew: boolean;
  loading: boolean;
  index: number;
  data: ValueCheck[];
  delData: ValueCheck[];
  modalData?: ValueCheck;
  diffVisible: boolean;
  diffMaster?: any;
  diffLocal?: any;
  diffRevert?: () => void;
}


export class ValueCheckTable extends React.Component<ValueCheckTableProps, ValueCheckTableState> {
  constructor(props: ValueCheckTableProps) {
    super(props);

    this.state = {
      loading: false,
      isNew: false,
      index: -1,
      data: [],
      delData: [],
      diffVisible: false
    };
  }

  componentDidMount() {
    this.onInit();
  }

  shouldComponentUpdate(nextProps: ValueCheckTableProps, nextState: ValueCheckTableState, nextContext: any) {

    if (!isEqual(nextProps, this.props)) {
      setTimeout(this.onInit, 100);

      return true;
    }

    return !isEqual(nextState, this.state);
  }

  onInit = async () => {
    const props = this.props;
    this.setState({ data: props.data });
  }

  defaultModalData = () => {
    const { frame, forSlot } = this.props;
    return {
      ownerId: forSlot ? forSlot.id : frame.id,
      ownerType: forSlot ? ParameterType.ATTRIBUTE : frame.type,
      frameId: frame.id,
      id: '',
      sourceType: SourceType.CODE_EXPRESSION,
      source: '',
      variable: [],
      parameters: [],
      prompts: [],
      recovering: true,
      recoveringActions: forSlot ? [{
        type: ActionType.CLEAR_SLOT as any,
        slot: forSlot.customizationPath ? forSlot.customizationPath : [{
          id: forSlot.id,
          type: ParameterType.ATTRIBUTE,
          name: forSlot.name
        }]
      }] : [],
      state: DiffStates.ADD
    };
  }

  promptRef = React.createRef<PromptListHandle>();

  add = () => {
    this.setState({
      modalData: this.defaultModalData(),
      isNew: true
    });
  }

  render() {
    const {
      loading,
      data,
      modalData,
      index
    } = this.state;
    const { frame, parameters, selectedParameters, disabled, forSlot, showSwitch, switchChecked, onSwitchChange } = this.props;

    const modalDisabled = modalData && disabled;
    return <NiSpin spinning={loading}>
      <BodyGroup
        noPadding
        foldable
        defaultFolded
        // className={`slot-suggestion-card ${!switchChecked && 'no-content'}`}
        title={localizedString('value_check')}
        titleExtra={<Space size={12}>
          <HelperPopover localizedKey="slot_detail_helper_value_check" />
          {
            showSwitch &&
            <Switch disabled={(!!forSlot && !!frame.originKey) || disabled} checked={switchChecked} onChange={onSwitchChange} />
          }
        </Space>
        }
        extra={[
          // switchChecked &&
          data && data.filter(item => !item.toBeDeleted).length &&
          <AddButton
            disabled={disabled}
            onClick={this.add} /> || ''
          //   ,
          // <AnnotationOperator
          //   disabled={disabled  || (!!forSlot && !!frame.originKey)}
          //   onDelete={() => {
          //     if (this.props.cardDeleteClick) {
          //       this.props.cardDeleteClick();
          //     }
          //   }}
          // />
        ]
        }
      >
        {modalData && <NiModal
          title={localizedString('value_check')}
          width={'60%'}
          wrapClassName="value-check-modal"
          open={!!modalData}
          onCancel={this.handleModalCancel}
          // onOk={modalDisabled ? undefined : this.handleModalConfirm}
          // okButtonProps={modalDisabled ? {
          //   type: 'ghost',
          //   disabled: true
          // } : undefined}

          okText={(modalData.inheritedFromCompositeId && !modalDisabled) &&
            <Popconfirm
              title={
                <span>{localizedString('value_suggestion_inherit_copy_popconfirm')}
                  <HelperPopover localizedKey="value_suggestion_inherit_copy_popover" />
                </span>
              }
              okButtonProps={{
                disabled: isEqual(data[index], modalData)
              }}
              onConfirm={this.handleModalConfirm}
            >
              <a href="#">{localizedString('common_confirm')}</a>
            </Popconfirm> || undefined}
          onOk={() => {
            if (modalData.inheritedFromCompositeId ||
              modalDisabled ||
              isEqual(data[index], modalData)) {
              return;
            }

            this.handleModalConfirm();
          }}
          okButtonProps={
            modalDisabled ||
              isEqual(data[index], modalData) ? {
              type: 'ghost',
              disabled: true
            } : undefined}
        >
          <NiForm
            titleline
            direction="vertical"
            items={[
              {
                key: 'func-slot',
                label: localizedString('value_check_function_slot'),
                content: <Space style={{ width: '100%' }} direction="vertical">
                  <CodeTextArea
                    inModal
                    dataId="source"
                    disabled={disabled}
                    language="kotlin"
                    data={modalData.source}
                    update={(nextSource: string) => {
                      const next = cloneDeep(modalData);
                      next.source = nextSource;
                      this.setState({
                        modalData: next
                      });
                    }}
                    completionContext={
                      {
                        rawCodeType: frame
                      }
                    }
                  />
                  {/* {<ReferenceTextArea
                    initParameters={selectedParameters}
                    parameterMap={ParameterMapStore.all()}
                    allowNewLine={true}
                    rawCodeMode={true}
                    rawCodeType={frame}
                    disabled={modalDisabled || !!modalData.originKey}
                    placeholder={localizedString('condition_add')}
                    className="reset"
                    data={modalData.source}
                    update={(nextSource: string) => {
                      const next = cloneDeep(modalData);
                      next.source = nextSource;
                      this.setState({
                        modalData: next
                      });
                    }}
                    comment={modalData.sourceComment}
                    onCommentChange={(nextComment: string) => {
                      const next = cloneDeep(modalData);
                      next.sourceComment = nextComment;
                      this.setState({
                        modalData: next
                      });
                    }}
                  />
                  } */}
                </Space>
              },
              {
                key: 'prompts',
                label: localizedString('attribute_inform_channels'),
                extra: modalData.prompts && modalData.prompts.filter(item => !item.toBeDeleted).length && <AddButton disabled={disabled || isStructAgent()}
                  onClick={(e: any) => {
                    this.promptRef.current?.add(e);
                  }} /> || '',
                content: <NewPromptList
                  inModal
                  ref={this.promptRef}
                  style={{ flex: 1 }}
                  agentId={getAgentId()}
                  frameId={frame.id || ''}
                  ownerId={modalData.id || ''}
                  ownerType={PromptOwnerType.valueCheck}
                  promptType={PromptType.error}
                  frame={frame}
                  disableChange={isStructAgent() || modalDisabled}
                  noReference={false}
                  isSingleLine={false}
                  allowClearAll={true}
                  data={modalData.prompts}
                  parameterMap={ParameterMapStore.all()}
                  parameters={selectedParameters.map(attributeToParameter)}
                  update={(prompts: PromptInfo[]) => {
                    const nextModalData = cloneDeep(modalData);
                    nextModalData.prompts = [...prompts];
                    this.setState({
                      modalData: nextModalData
                    });
                  }}
                />
              }, {
                key: 'recovering',
                label: localizedString('value_check_recovering'),
                content: this.actionsComp(),
                extra: <NiSelect
                  value={null as any}
                  placeholder={localizedString('response_add_placeholder')}
                  onChange={() => {
                    const nextModalData = cloneDeep(modalData);

                    // if (forSlot && (!modalData.recoveringActions || modalData.recoveringActions.length === 0)) {

                    nextModalData.recoveringActions.push({
                      type: ActionType.CLEAR_SLOT as any,
                      // slot: [{
                      //   id: forSlot.id || '',
                      //   type: ParameterType.ATTRIBUTE,
                      //   name: forSlot.name || ''
                      // }]
                    });
                    // }

                    this.setState({
                      modalData: nextModalData
                    });
                  }}>
                  <NiSelect.Option key="clear-slot">{localizedString('response_type_clear_slot_action')}</NiSelect.Option>
                </NiSelect>
              }
            ]}
          />
        </NiModal>
        }
        {
          // switchChecked &&
          data && data.filter(item => !item.toBeDeleted).length &&
          <NiTable
            rowKey={(record: ValueCheck) => `value-check-${record.id}`}
            dataSource={data}
            pagination={false}
            onRow={(record: ValueCheck, index?: number) => ({
              onClick: () => {
                const modalData = cloneDeep(record);

                if (forSlot && (!modalData.recoveringActions || modalData.recoveringActions.length === 0)) {

                  modalData.recoveringActions = [{
                    type: ActionType.CLEAR_SLOT as any,
                    slot: [{
                      id: forSlot.id || '',
                      type: ParameterType.ATTRIBUTE,
                      name: forSlot.name || ''
                    }]
                  }];
                }

                this.setState({
                  modalData,
                  index: index || 0,
                  isNew: false
                });
              }
            })}
            columns={this.getColumns()}
          /> || <EmptyLine text="Click to add a system validation" onClick={this.add} />
        }
      </BodyGroup >
    </NiSpin >;
  }

  actionsComp = () => {
    const { disabled } = this.props;
    const { modalData } = this.state;

    if (!modalData) {
      return '';
    }

    return <div className="row-container" key="actions-wrapper">
      <Space className="row-content" direction="vertical" style={{ width: '100%' }} size={12}>
        {
          this.getActionsDom(modalData.recoveringActions)
        }
      </Space>
    </div>;
  }

  getActionsDom(actions: FramelyAction[]) {

    const responseSequenceDom = [];
    for (const [i, response] of (actions || []).entries()) {
      switch (response.type) {
        case 'clear_slot_action':
          responseSequenceDom.push(
            this.getClearSlotActionDiv(i, response)
          );
          break;
        default:
          break;
      }
    }

    return responseSequenceDom;
  }

  getClearSlotActionDiv = (idx: number, action: ClearSlotAction) => {
    const { disabled, frame } = this.props;
    const { modalData } = this.state;

    if (!modalData) {
      return '';
    }

    return (
      <NiCard
        title={localizedString('response_type_clear_slot_action')}
        className="response-card"
        key={`response-block-${ActionType.CLEAR_SLOT}-${idx}`}
        extra={disabled ? [] : <NiButton
          style={{
            border: 0
          }}
          onClick={this.handleDeleteAction.bind(this, idx)}
        >
          <DeleteOutlined />
        </NiButton>}
        hoverExtra={true}
      >
        <NiForm
          items={[{
            key: 'intent',
            label: localizedString('target_slot'),
            content: <NiCommonCascader
              allowClear={false}
              disabled={disabled}
              selectionkey={SELECTION_KEY.AVAILABLE_SLOT}
              key={SELECTION_KEY.AVAILABLE_SLOT}
              rootpath={[{
                value: frame.id as string,
                value_type: frame.type
              }]}
              value={action.slot}
              onChange={(value: string[], selectedOptions?: SelectionOption[]) => {

                if (!selectedOptions) {
                  return;
                }

                const ref: Reference = selectedOptions.map((opt: SelectionOption) => ({
                  id: opt.value,
                  name: opt.label,
                  type: opt.valueType
                })) as Reference;

                this.handleUpdateActionByField(idx, action, 'slot', ref);
              }}
            />
          }]}
        />
      </NiCard>
    );
  };

  handleDeleteAction = (idx: number) => {
    const { modalData } = this.state;
    const target = cloneDeep(modalData);

    if (!target) {
      return;
    }

    if (target) {
      target.recoveringActions.splice(idx, 1);
    }

    this.setState({
      modalData: target
    });
  };

  handleUpdateActionByField = (
    idx: number,
    action: any,
    field: string,
    nextValue: any
  ) => {

    if (action[field] === nextValue) {
      return;
    }

    const nextAction = cloneDeep(action);
    nextAction[field] = nextValue;
    this.handleUpdateAction(idx, nextAction);
  }

  handleUpdateAction = (idx: number, action: FramelyAction) => {
    const { modalData } = this.state;
    const target = cloneDeep(modalData);

    if (!target) {
      return;
    }

    target.recoveringActions[idx] = action;

    this.setState({
      modalData: target
    });
  }

  getColumns = () => {
    const { disabled } = this.props;
    return [
      {
        // width: '90%',
        title: localizedString('value_check_function_slot'),
        dataIndex: 'variable',
        render: (value: Reference, record: ValueCheck) =>
          record.sourceType === SourceType.CODE_EXPRESSION
            ? record.source
            : value.map((node: ReferenceNode) => node.name).join('.')
      },
      {
        width: 150,
        title: 'Source',
        dataIndex: 'inheritedFromCompositeName',
        key: 'source',
        render: (sourceName: string, record: ValueCheck, index: number) => {
          return sourceRender(record, this.props.frame.type);
        }
      },
      // admin
      {
        title: '',
        className: 'hide',
        key: 'operation',
        width: 70,
        render: (
          operation: any,
          record: ValueCheck,
          index: number
        ) => {
          return (
            <div className="icon-area">
              {!disabled &&
                !record.originKey &&
                !record.inheritedFromCompositeId &&
                <DeleteOutlined
                  className="delete-btn"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    this.handleDelete(record, index);
                  }}
                />
              }
            </div>
          );
        }
      }
    ];
  };

  handleDelete = async (record: ValueCheck, index: number) => {
    const props = this.props;
    const nextData = cloneDeep(this.state.data);

    this.setState({ loading: true });

    await API.valueCheck.delete({
      metaID: record.id || '',
      ownerID: record.ownerId,
      frameID: record.frameId
    }).catch((err: any) => {
      message.error(err.message);
      this.setState({ loading: false });
      throw new Error(err.message);
    });

    this.setState({ loading: false });

    nextData.splice(index, 1);
    this.setState({
      data: nextData
    }, () => {
      if (props.didChange) {
        props.didChange(nextData);
      }
    });
  }

  modalDirtyCheck = () => {
    const { isNew, modalData, index, data } = this.state;

    if (!modalData) {
      return false;
    }

    const oriData = isNew ? this.defaultModalData() : data[index];
    return !isEqual(oriData.variable, modalData.variable) ||
      !isEqual(oriData.prompts, modalData.prompts);
  }

  handleModalCancel = () => {
    if (this.modalDirtyCheck()) {
      discardModal(() => {
        this.setState({ modalData: undefined });
      });
      return;
    }
    this.setState({ modalData: undefined });
  }

  handleModalConfirm = debounce(async () => {
    const { modalData, data, isNew, index } = this.state;
    const props = this.props;

    if (!modalData) {
      return;
    }

    const otherData = cloneDeep(data);

    if (!isNew) {
      otherData.splice(index, 1);
    }

    // let variablesRepeated = false;

    // for (const item of otherData) {
    //   if (isEqual(item.variable, modalData.variable)) {
    //     variablesRepeated = true;
    //     break;
    //   }
    // }

    // if (variablesRepeated) {
    //   message.warning('The slots are repeated');
    //   return;
    // }

    switch (modalData.sourceType) {
      case SourceType.CODE_EXPRESSION:
        if (!modalData.source || !modalData.source.length) {
          message.warning('Please enter the cource expression');
          return;
        }
        break;
      default:
        break;
    }

    if (!modalData.prompts || !modalData.prompts.length) {
      message.warning('Please add an prompt!');
      return;
    }

    const nextItem = cloneDeep(modalData);
    nextItem.prompts = modalData.prompts.filter(Boolean);

    const payload = valueCheckToServer(nextItem);

    if (isNew) {

      const nextData = cloneDeep(data);

      this.setState({ loading: true });

      await API.valueCheck.create(payload).catch((err: any) => {
        message.error(err.message);
        this.setState({ loading: false });
        throw new Error(err.message);
      });

      this.setState({ loading: false });

      nextData.push(nextItem);

      this.setState({
        data: nextData,
        modalData: undefined
      }, () => {
        if (props.didChange) {
          props.didChange(nextData);
        }
      });
    } else {
      await API.valueCheck.update({
        metaID: nextItem.id || '',
        ownerID: nextItem.ownerId,
        ownerType: nextItem.ownerType,
        frameID: nextItem.frameId
      },
        payload,
        nextItem.inheritedFromCompositeId && {
          metaID: nextItem.id || '',
          ownerID: props.forSlot ? props.forSlot.id : props.frame.id,
          ownerType: props.forSlot ? PAGE_TYPE.ATTRIBUTE : props.frame.type,
          frameID: props.frame.id
        } || undefined
      ).catch((err: any) => {
        message.error(err.message);
        this.setState({ loading: false });
        throw new Error(err.message);
      });

      this.setState({ loading: false });

      const nextData = cloneDeep(data);
      nextData[index] = nextItem;

      this.setState({
        data: nextData,
        modalData: undefined
      }, () => {
        if (props.didChange) {
          props.didChange(nextData);
        }
      });
    }
  }, 500);
}
